import api from './authService'
import { crearErrorAplicacionApi, obtenerMensajeErrorApi } from './apiError'

export interface ConfiguracionGeneral {
  nombreSistema: string
  descripcionSistema: string
  idiomaDefecto: string
  zonaHoraria: string
  formatoFecha: string
  modoMantenimiento: boolean
  registroPublico: boolean
  verificacionEmail: boolean
}

export interface ConfiguracionSeguridad {
  longitudMinimaPassword: number
  requiereCaracteresEspeciales: boolean
  requiereNumeros: boolean
  requiereMayusculas: boolean
  expiracionSesion: number
  intentosMaximosLogin: number
  tiempoBloqueo: number
  autenticacionDosFactor: boolean
}

export interface ConfiguracionEmail {
  servidorSMTP: string
  puertoSMTP: number
  usuarioSMTP: string
  passwordSMTP: string
  encriptacion: 'none' | 'tls' | 'ssl'
  emailRemitente: string
  nombreRemitente: string
  plantillaBienvenida: string
  plantillaRecuperacion: string
  notificacionesEmail: boolean
}

export interface ConfiguracionAlmacenamiento {
  tipoAlmacenamiento: 'local' | 's3' | 'azure' | 'gcp'
  rutaLocal?: string
  bucketS3?: string
  regionS3?: string
  accessKeyS3?: string
  secretKeyS3?: string
  tamañoMaximoArchivo: number
  tiposArchivosPermitidos: string[]
  compresionAutomatica: boolean
  respaldoAutomatico: boolean
}

export interface ConfiguracionNotificaciones {
  notificacionesEmail: boolean
  notificacionesPush: boolean
  notificacionesSlack: boolean
  webhookSlack?: string
  notificacionesDiscord: boolean
  webhookDiscord?: string
  frecuenciaReportes: 'diario' | 'semanal' | 'mensual'
  horaEnvioReportes: string
}

export interface ParametroPersonalizado {
  id: string
  clave: string
  valor: string
  tipo: 'string' | 'number' | 'boolean' | 'json'
  descripcion?: string
  categoria?: string
  fechaCreacion: string
  fechaModificacion?: string
}

export const configuracionService = {
  /**
   * Obtener configuración general
   */
  async obtenerConfiguracionGeneral(): Promise<ConfiguracionGeneral> {
    try {
      const response = await api.get('/api/configuracion/general')
      const config = response.data
      return {
        nombreSistema: config.nombre_sistema || config.system_name || 'LogiXPort',
        descripcionSistema: config.descripcion_sistema || config.system_description || '',
        idiomaDefecto: config.idioma_defecto || config.default_language || 'es',
        zonaHoraria: config.zona_horaria || config.timezone || 'America/Mexico_City',
        formatoFecha: config.formato_fecha || config.date_format || 'DD/MM/YYYY',
        modoMantenimiento: config.modo_mantenimiento || config.maintenance_mode || false,
        registroPublico: config.registro_publico || config.public_registration || true,
        verificacionEmail: config.verificacion_email || config.email_verification || false,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener configuración general')
    }
  },

  /**
   * Actualizar configuración general
   */
  async actualizarConfiguracionGeneral(config: ConfiguracionGeneral): Promise<ConfiguracionGeneral> {
    try {
      // Mapear al formato que espera el backend (snake_case)
      const payload = {
        nombre_sistema: config.nombreSistema,
        descripcion_sistema: config.descripcionSistema,
        idioma_defecto: config.idiomaDefecto,
        zona_horaria: config.zonaHoraria,
        formato_fecha: config.formatoFecha,
        modo_mantenimiento: config.modoMantenimiento,
        registro_publico: config.registroPublico,
        verificacion_email: config.verificacionEmail
      }
      
      await api.put('/api/configuracion/general', payload)
      return config
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar configuración general')
    }
  },

  /**
   * Obtener configuración de seguridad
   */
  async obtenerConfiguracionSeguridad(): Promise<ConfiguracionSeguridad> {
    try {
      const response = await api.get('/api/configuracion/seguridad')
      const config = response.data
      return {
        longitudMinimaPassword: config.longitud_minima_password || config.min_password_length || 8,
        requiereCaracteresEspeciales: config.requiere_caracteres_especiales || config.require_special_chars || false,
        requiereNumeros: config.requiere_numeros || config.require_numbers || false,
        requiereMayusculas: config.requiere_mayusculas || config.require_uppercase || false,
        expiracionSesion: config.expiracion_sesion || config.session_expiration || 24,
        intentosMaximosLogin: config.intentos_maximos_login || config.max_login_attempts || 5,
        tiempoBloqueo: config.tiempo_bloqueo || config.lockout_time || 30,
        autenticacionDosFactor: config.autenticacion_dos_factor || config.two_factor_auth || false,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener configuración de seguridad')
    }
  },

  /**
   * Actualizar configuración de seguridad
   */
  async actualizarConfiguracionSeguridad(config: ConfiguracionSeguridad): Promise<ConfiguracionSeguridad> {
    try {
      const response = await api.put('/api/configuracion/seguridad', {
        longitud_minima_password: config.longitudMinimaPassword,
        requiere_caracteres_especiales: config.requiereCaracteresEspeciales,
        requiere_numeros: config.requiereNumeros,
        requiere_mayusculas: config.requiereMayusculas,
        expiracion_sesion: config.expiracionSesion,
        intentos_maximos_login: config.intentosMaximosLogin,
        tiempo_bloqueo: config.tiempoBloqueo,
        autenticacion_dos_factor: config.autenticacionDosFactor,
      })
      const updatedConfig = response.data
      return {
        longitudMinimaPassword: updatedConfig.longitud_minima_password || updatedConfig.min_password_length,
        requiereCaracteresEspeciales: updatedConfig.requiere_caracteres_especiales || updatedConfig.require_special_chars,
        requiereNumeros: updatedConfig.requiere_numeros || updatedConfig.require_numbers,
        requiereMayusculas: updatedConfig.requiere_mayusculas || updatedConfig.require_uppercase,
        expiracionSesion: updatedConfig.expiracion_sesion || updatedConfig.session_expiration,
        intentosMaximosLogin: updatedConfig.intentos_maximos_login || updatedConfig.max_login_attempts,
        tiempoBloqueo: updatedConfig.tiempo_bloqueo || updatedConfig.lockout_time,
        autenticacionDosFactor: updatedConfig.autenticacion_dos_factor || updatedConfig.two_factor_auth,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar configuración de seguridad')
    }
  },

  /**
   * Obtener configuración de email
   */
  async obtenerConfiguracionEmail(): Promise<ConfiguracionEmail> {
    try {
      const response = await api.get('/api/configuracion/email')
      const config = response.data
      return {
        servidorSMTP: config.servidor_smtp || config.smtp_server || '',
        puertoSMTP: config.puerto_smtp || config.smtp_port || 587,
        usuarioSMTP: config.usuario_smtp || config.smtp_user || '',
        passwordSMTP: config.password_smtp || config.smtp_password || '',
        encriptacion: config.encriptacion || config.encryption || 'tls',
        emailRemitente: config.email_remitente || config.sender_email || '',
        nombreRemitente: config.nombre_remitente || config.sender_name || '',
        plantillaBienvenida: config.plantilla_bienvenida || config.welcome_template || '',
        plantillaRecuperacion: config.plantilla_recuperacion || config.recovery_template || '',
        notificacionesEmail: config.notificaciones_email || config.email_notifications || true,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener configuración de email')
    }
  },

  /**
   * Actualizar configuración de email
   */
  async actualizarConfiguracionEmail(config: ConfiguracionEmail): Promise<ConfiguracionEmail> {
    try {
      const response = await api.put('/api/configuracion/email', {
        servidor_smtp: config.servidorSMTP,
        puerto_smtp: config.puertoSMTP,
        usuario_smtp: config.usuarioSMTP,
        password_smtp: config.passwordSMTP,
        encriptacion: config.encriptacion,
        email_remitente: config.emailRemitente,
        nombre_remitente: config.nombreRemitente,
        plantilla_bienvenida: config.plantillaBienvenida,
        plantilla_recuperacion: config.plantillaRecuperacion,
        notificaciones_email: config.notificacionesEmail,
      })
      const updatedConfig = response.data
      return {
        servidorSMTP: updatedConfig.servidor_smtp || updatedConfig.smtp_server,
        puertoSMTP: updatedConfig.puerto_smtp || updatedConfig.smtp_port,
        usuarioSMTP: updatedConfig.usuario_smtp || updatedConfig.smtp_user,
        passwordSMTP: updatedConfig.password_smtp || updatedConfig.smtp_password,
        encriptacion: updatedConfig.encriptacion || updatedConfig.encryption,
        emailRemitente: updatedConfig.email_remitente || updatedConfig.sender_email,
        nombreRemitente: updatedConfig.nombre_remitente || updatedConfig.sender_name,
        plantillaBienvenida: updatedConfig.plantilla_bienvenida || updatedConfig.welcome_template,
        plantillaRecuperacion: updatedConfig.plantilla_recuperacion || updatedConfig.recovery_template,
        notificacionesEmail: updatedConfig.notificaciones_email || updatedConfig.email_notifications,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar configuración de email')
    }
  },

  /**
   * Obtener configuración de almacenamiento
   */
  async obtenerConfiguracionAlmacenamiento(): Promise<ConfiguracionAlmacenamiento> {
    try {
      const response = await api.get('/api/configuracion/almacenamiento')
      const config = response.data
      return {
        tipoAlmacenamiento: config.tipo_almacenamiento || config.storage_type || 'local',
        rutaLocal: config.ruta_local || config.local_path || '',
        bucketS3: config.bucket_s3 || config.s3_bucket || '',
        regionS3: config.region_s3 || config.s3_region || '',
        accessKeyS3: config.access_key_s3 || config.s3_access_key || '',
        secretKeyS3: config.secret_key_s3 || config.s3_secret_key || '',
        tamañoMaximoArchivo: config.tamano_maximo_archivo || config.max_file_size || 100,
        tiposArchivosPermitidos: config.tipos_archivos_permitidos || config.allowed_file_types || ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'png', 'gif'],
        compresionAutomatica: config.compresion_automatica || config.auto_compression || false,
        respaldoAutomatico: config.respaldo_automatico || config.auto_backup || false,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener configuración de almacenamiento')
    }
  },

  /**
   * Actualizar configuración de almacenamiento
   */
  async actualizarConfiguracionAlmacenamiento(config: ConfiguracionAlmacenamiento): Promise<ConfiguracionAlmacenamiento> {
    try {
      const response = await api.put('/api/configuracion/almacenamiento', {
        tipo_almacenamiento: config.tipoAlmacenamiento,
        ruta_local: config.rutaLocal,
        bucket_s3: config.bucketS3,
        region_s3: config.regionS3,
        access_key_s3: config.accessKeyS3,
        secret_key_s3: config.secretKeyS3,
        tamano_maximo_archivo: config.tamañoMaximoArchivo,
        tipos_archivos_permitidos: config.tiposArchivosPermitidos,
        compresion_automatica: config.compresionAutomatica,
        respaldo_automatico: config.respaldoAutomatico,
      })
      const updatedConfig = response.data
      return {
        tipoAlmacenamiento: updatedConfig.tipo_almacenamiento || updatedConfig.storage_type,
        rutaLocal: updatedConfig.ruta_local || updatedConfig.local_path,
        bucketS3: updatedConfig.bucket_s3 || updatedConfig.s3_bucket,
        regionS3: updatedConfig.region_s3 || updatedConfig.s3_region,
        accessKeyS3: updatedConfig.access_key_s3 || updatedConfig.s3_access_key,
        secretKeyS3: updatedConfig.secret_key_s3 || updatedConfig.s3_secret_key,
        tamañoMaximoArchivo: updatedConfig.tamano_maximo_archivo || updatedConfig.max_file_size,
        tiposArchivosPermitidos: updatedConfig.tipos_archivos_permitidos || updatedConfig.allowed_file_types,
        compresionAutomatica: updatedConfig.compresion_automatica || updatedConfig.auto_compression,
        respaldoAutomatico: updatedConfig.respaldo_automatico || updatedConfig.auto_backup,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar configuración de almacenamiento')
    }
  },

  /**
   * Obtener configuración de notificaciones
   */
  async obtenerConfiguracionNotificaciones(): Promise<ConfiguracionNotificaciones> {
    try {
      const response = await api.get('/api/configuracion/notificaciones')
      const config = response.data
      return {
        notificacionesEmail: config.notificaciones_email || config.email_notifications || false,
        notificacionesPush: config.notificaciones_push || config.push_notifications || false,
        notificacionesSlack: config.notificaciones_slack || config.slack_notifications || false,
        webhookSlack: config.webhook_slack || config.slack_webhook || '',
        notificacionesDiscord: config.notificaciones_discord || config.discord_notifications || false,
        webhookDiscord: config.webhook_discord || config.discord_webhook || '',
        frecuenciaReportes: config.frecuencia_reportes || config.report_frequency || 'semanal',
        horaEnvioReportes: config.hora_envio_reportes || config.report_time || '09:00',
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener configuración de notificaciones')
    }
  },

  /**
   * Actualizar configuración de notificaciones
   */
  async actualizarConfiguracionNotificaciones(config: ConfiguracionNotificaciones): Promise<ConfiguracionNotificaciones> {
    try {
      const response = await api.put('/api/configuracion/notificaciones', {
        notificaciones_email: config.notificacionesEmail,
        notificaciones_push: config.notificacionesPush,
        notificaciones_slack: config.notificacionesSlack,
        webhook_slack: config.webhookSlack,
        notificaciones_discord: config.notificacionesDiscord,
        webhook_discord: config.webhookDiscord,
        frecuencia_reportes: config.frecuenciaReportes,
        hora_envio_reportes: config.horaEnvioReportes,
      })
      const updatedConfig = response.data
      return {
        notificacionesEmail: updatedConfig.notificaciones_email || updatedConfig.email_notifications,
        notificacionesPush: updatedConfig.notificaciones_push || updatedConfig.push_notifications,
        notificacionesSlack: updatedConfig.notificaciones_slack || updatedConfig.slack_notifications,
        webhookSlack: updatedConfig.webhook_slack || updatedConfig.slack_webhook,
        notificacionesDiscord: updatedConfig.notificaciones_discord || updatedConfig.discord_notifications,
        webhookDiscord: updatedConfig.webhook_discord || updatedConfig.discord_webhook,
        frecuenciaReportes: updatedConfig.frecuencia_reportes || updatedConfig.report_frequency,
        horaEnvioReportes: updatedConfig.hora_envio_reportes || updatedConfig.report_time,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar configuración de notificaciones')
    }
  },

  /**
   * Probar conexión de email
   */
  async probarConexionEmail(): Promise<{ exito: boolean; mensaje: string }> {
    try {
      const response = await api.post('/api/configuracion/email/probar')
      return {
        exito: response.data.success || response.data.exito || true,
        mensaje: response.data.message || response.data.mensaje || 'Conexión exitosa',
      }
    } catch (error: unknown) {
      const message = obtenerMensajeErrorApi(error, 'Error al probar conexión de email')
      return {
        exito: false,
        mensaje: message,
      }
    }
  },

  /**
   * Obtener parámetros personalizados
   */
  async obtenerParametrosPersonalizados(): Promise<ParametroPersonalizado[]> {
    try {
      const response = await api.get('/api/configuracion/parametros')
      return response.data.map((param: any) => this.normalizarParametro(param))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener parámetros personalizados')
    }
  },

  /**
   * Crear parámetro personalizado
   */
  async crearParametroPersonalizado(param: Omit<ParametroPersonalizado, 'id' | 'fechaCreacion' | 'fechaModificacion'>): Promise<ParametroPersonalizado> {
    try {
      const response = await api.post('/api/configuracion/parametros', {
        clave: param.clave,
        valor: param.valor,
        tipo: param.tipo,
        descripcion: param.descripcion,
        categoria: param.categoria,
      })
      return this.normalizarParametro(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al crear parámetro personalizado')
    }
  },

  /**
   * Actualizar parámetro personalizado
   */
  async actualizarParametroPersonalizado(id: string, param: Partial<ParametroPersonalizado>): Promise<ParametroPersonalizado> {
    try {
      const response = await api.put(`/api/configuracion/parametros/${id}`, {
        clave: param.clave,
        valor: param.valor,
        tipo: param.tipo,
        descripcion: param.descripcion,
        categoria: param.categoria,
      })
      return this.normalizarParametro(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar parámetro personalizado')
    }
  },

  /**
   * Eliminar parámetro personalizado
   */
  async eliminarParametroPersonalizado(id: string): Promise<void> {
    try {
      await api.delete(`/api/configuracion/parametros/${id}`)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al eliminar parámetro personalizado')
    }
  },

  /**
   * Normalizar parámetro del backend al frontend
   */
  normalizarParametro(param: any): ParametroPersonalizado {
    return {
      id: String(param.id),
      clave: param.clave || param.key,
      valor: param.valor || param.value,
      tipo: param.tipo || param.type || 'string',
      descripcion: param.descripcion || param.description,
      categoria: param.categoria || param.category,
      fechaCreacion: param.fecha_creacion || param.created_at || new Date().toISOString(),
      fechaModificacion: param.fecha_modificacion || param.updated_at,
    }
  },
}
